import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Avoir } from "./models/AvoirModel"

export interface AvoirWithAnnonce extends Avoir {
  annonce?: {
    id: number
    firstname: string
    name: string
    email: string
    numAnnonce: string
    numDossier: string
    type_annonce: string
  }
}

export interface AvoirStats {
  total: number
  pending: number
  utilise: number
  a_rembourser: number
  rembourse: number
  montant_total: number
}

interface AvoirState {
  avoirs: AvoirWithAnnonce[]
  stats: AvoirStats | null
  loading: boolean
  error: string | null
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
  } | null
}

const initialState: AvoirState = {
  avoirs: [],
  stats: null,
  loading: false,
  error: null,
  pagination: null,
}

export const fetchAllAvoirs = createAsyncThunk(
  "avoir/fetchAll",
  async (params: { page?: number; limit?: number; statut?: string } = {}) => {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append("page", params.page.toString())
    if (params.limit) queryParams.append("limit", params.limit.toString())
    if (params.statut) queryParams.append("statut", params.statut)

    const response = await fetch(`/api/avoirs?${queryParams.toString()}`)
    if (!response.ok) throw new Error("Erreur lors de la récupération des avoirs")
    return response.json()
  },
)

export const fetchAvoirStats = createAsyncThunk("avoir/fetchStats", async () => {
  const response = await fetch("/api/avoirs/stats")
  if (!response.ok) throw new Error("Erreur lors de la récupération des statistiques")
  return response.json()
})

export const createAvoir = createAsyncThunk(
  "avoir/create",
  async (avoirData: { annonce_id: number; montant_avoir: number; statut: string }) => {
    const response = await fetch("/api/avoirs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(avoirData),
    })
    if (!response.ok) throw new Error("Erreur lors de la création de l'avoir")
    return response.json()
  },
)

export const updateAvoir = createAsyncThunk(
  "avoir/update",
  async ({ id, data }: { id: number; data: Partial<Avoir> }) => {
    const response = await fetch(`/api/avoirs/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error("Erreur lors de la mise à jour de l'avoir")
    return response.json()
  },
)

export const deleteAvoir = createAsyncThunk("avoir/delete", async (id: number) => {
  const response = await fetch(`/api/avoirs/${id}`, { method: "DELETE" })
  if (!response.ok) throw new Error("Erreur lors de la suppression de l'avoir")
  return { id }
})

const avoirSlice = createSlice({
  name: "avoir",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateAvoirStatus: (state, action: PayloadAction<{ id: number; statut: string }>) => {
      const avoir = state.avoirs.find((a) => a.id === action.payload.id)
      if (avoir) {
        avoir.statut = action.payload.statut as any
      }
    },
    removeAvoir: (state, action: PayloadAction<number>) => {
      state.avoirs = state.avoirs.filter((avoir) => avoir.id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    // Fetch all avoirs
    builder
      .addCase(fetchAllAvoirs.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllAvoirs.fulfilled, (state, action) => {
        state.loading = false
        state.avoirs = action.payload.avoirs
        state.pagination = action.payload.pagination
      })
      .addCase(fetchAllAvoirs.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || "Erreur inconnue"
      })

    // Fetch stats
    builder.addCase(fetchAvoirStats.fulfilled, (state, action) => {
      state.stats = action.payload.stats
    })

    // Create avoir
    builder.addCase(createAvoir.fulfilled, (state, action) => {
      state.avoirs.unshift(action.payload.avoir)
    })

    // Update avoir
    builder.addCase(updateAvoir.fulfilled, (state, action) => {
      const index = state.avoirs.findIndex((a) => a.id === action.payload.avoir.id)
      if (index !== -1) {
        state.avoirs[index] = { ...state.avoirs[index], ...action.payload.avoir }
      }
    })

    // Delete avoir
    builder.addCase(deleteAvoir.fulfilled, (state, action) => {
      state.avoirs = state.avoirs.filter((avoir) => avoir.id !== action.payload.id)
    })
  },
})

export const { clearError, updateAvoirStatus, removeAvoir } = avoirSlice.actions
export default avoirSlice.reducer
